import React, { useEffect, useState } from 'react';
import {
  ButtonHeadless,
  InputLabel,
  Modal,
  TextInput,
} from '@wajeez-web/components/ui';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { ControllerFieldState } from 'react-hook-form';
import { AiFillCloseCircle } from 'react-icons/ai';
import { useDebounce } from '@wajeez-web/components/helpers';

export type ModalSelectOption<TId> = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: TId;
  label: string | undefined;
  group?: string;
};

export interface ModalSelectProps<TId> {
  options: ModalSelectOption<TId>[];
  onChange: (value: TId | undefined | null) => void;
  defaultOptions?: ModalSelectOption<TId>[];
  value?: TId | undefined;
  onSearch?: (searchText: string | undefined) => void;
  onRender?: () => void | Promise<void>;
  label?: string;
  required?: boolean;
  isError?: boolean;
  fieldState?: ControllerFieldState;
}

export const ModalSelectInput = <TId,>({
  onRender,
  onSearch,
  options,
  defaultOptions,
  onChange,
  value,
  label,
  required,
  isError,
  fieldState,
}: ModalSelectProps<TId>) => {
  const selected = defaultOptions?.find((f) => f.value === value);

  const [
    isFlowComponentsGeneralModalOpen,
    setIsFlowComponentsGeneralModalOpen,
  ] = useState(false);

  return (
    <>
      <div className="relative w-full">
        <InputLabel text={label} name={''} required={required} />
        <div className="">
          {!selected?.value ? (
            <div className="w-[65px]">
              <ButtonHeadless
                paddingYStyle="py-1"
                action={() => {
                  if (isFlowComponentsGeneralModalOpen === false && onRender) {
                    onRender();
                  }

                  setIsFlowComponentsGeneralModalOpen(true);
                }}
              >
                <div className="text-white">
                  <ReturnTranslatedText
                    origintext="Choose"
                    text={`buttons.${'Choose'}`}
                  />
                </div>
              </ButtonHeadless>
            </div>
          ) : (
            <div className="w-full flex justify-start items-center">
              <div className="w-full max-w-[200px] py-4 px-3 rounded-md border text-center break-words">
                {selected.label}
              </div>
              <div
                className="ms-2 text-warmGray-400 cursor-pointer hover:opacity-75 active:opacity-100"
                onClick={() => {
                  onChange(null);
                }}
              >
                <AiFillCloseCircle />
              </div>
            </div>
          )}
        </div>
        {isError && (
          <div className="absolute top-[100%] right-0 text-[13px] text-red-500">
            <ReturnTranslatedText
              origintext="This field is required"
              text={`general.${'This field is required'}`}
            />
          </div>
        )}
      </div>
      {isFlowComponentsGeneralModalOpen && (
        <FlowComponentsGeneralModal
          isOpen={isFlowComponentsGeneralModalOpen}
          data={options}
          onClose={() => {
            console.log('onClose');

            setIsFlowComponentsGeneralModalOpen(false);
          }}
          onRender={onRender ?? (() => 0)}
          onSearch={onSearch ?? (() => 0)}
          onSubmit={(component) => {
            onChange(component?.value);

            console.log('submit');

            setIsFlowComponentsGeneralModalOpen(false);
          }}
        />
      )}
    </>
  );
};

interface FlowComponentsGeneralModalProps<TId> {
  isOpen: boolean;
  data: ModalSelectOption<TId>[];
  onClose: () => void;
  onRender: () => void;
  onSearch: (searchText: string | undefined) => void;
  onSubmit: (selectedComponent: ModalSelectOption<TId> | undefined) => void;
}

export const FlowComponentsGeneralModal = <TId,>({
  isOpen,
  data,
  onClose,
  onRender,
  onSearch,
  onSubmit,
}: FlowComponentsGeneralModalProps<TId>) => {
  const [selectedComponent, setSelectedComponent] =
    useState<ModalSelectOption<TId>>();

  const [setSearchTermDebounced] = useDebounce((data) => {
    onSearch(data?.value);
  });

  useEffect(() => {
    onRender();
  }, []);

  const renderComponents = () => {
    return data?.map((d) => {
      return (
        <div
          className={`w-full py-4 px-3 rounded-md border break-all text-center flex justify-center items-center cursor-pointer ${
            selectedComponent?.value === d.value && 'border-primary'
          }`}
          onClick={() => {
            setSelectedComponent(d);
          }}
        >
          {d.label}
        </div>
      );
    });
  };

  return (
    <Modal
      title="Choose Component"
      open={isOpen}
      onClose={() => {
        onClose();
        onRender();
      }}
    >
      <div className="w-full h-full">
        <div className="w-full max-w-[300px] px-2">
          <TextInput
            name="component-search"
            label="Search"
            onChange={(name, value) => {
              setSearchTermDebounced({ value });
            }}
          />
        </div>
        <div className="mt-5 px-2 w-full h-[380px] grid grid-cols-3 content-start items-stretch gap-x-4 gap-y-4 overflow-y-auto">
          {renderComponents()}
        </div>
        <div className="mt-6 w-full flex justify-end items-center">
          <div className="w-[70px]">
            <ButtonHeadless
              paddingYStyle="py-2"
              isDisabled={!selectedComponent}
              bgStyle={!selectedComponent ? 'bg-warmGray-300' : 'bg-primary'}
              action={() => {
                onSubmit(selectedComponent);
              }}
            >
              <div className="text-white">
                <ReturnTranslatedText
                  origintext="Choose"
                  text={`buttons.${'Choose'}`}
                />
              </div>
            </ButtonHeadless>
          </div>
        </div>
      </div>
    </Modal>
  );
};
